/**
 * The client register.
 *
 * Filtering is SERVER-side and allowlisted. `ClientViewSet` accepts exactly: `search`
 * (against reference, display name, contact name and contact email), `status`
 * (repeatable, and an unknown value is a 400 rather than a silent ignore),
 * `client_type`, `portal_enabled` and `owner` (or the literal "me"). There is no
 * `OrderingFilter` on this resource, so the columns are deliberately NOT sortable —
 * offering a sort the server would quietly ignore is worse than offering none.
 *
 * An empty result means "nothing matching that this reader may see". It does not mean
 * the firm has no clients, and the empty state must not speculate either way: a client
 * outside the reader's scope is absent from this list by design.
 */

import {
  STAFF,
  qk,
  usePaginatedList,
  type ClientListItem,
} from '@legalcrm/api';
import { Badge, Input, Button, Select, StatusPill, Table, type Column } from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { humanise } from '@/lib/format';
import { ImportCsvDialog } from '@/components/ImportCsvDialog';
import { PageHeader } from '@/shell/PageHeader';

import { useFirmMembers } from '../settings/useFirmMembers';

/** Mirrors `Client.Status`. Sent verbatim — the server validates against the same set. */
const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'prospective', label: 'Prospective' },
  { value: 'active', label: 'Active' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'archived', label: 'Archived' },
  { value: 'closed', label: 'Closed' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Any type' },
  { value: 'individual', label: 'Individual' },
  { value: 'organization', label: 'Organisation' },
];

const OWNER_OPTIONS = [
  { value: '', label: 'Everyone' },
  { value: 'me', label: 'Mine' },
];

/** A client's status is its own vocabulary, not one `statusTone` already knows. */
const STATUS_TONES = {
  prospective: 'info',
  active: 'success',
  dormant: 'warning',
  archived: 'neutral',
  closed: 'neutral',
} as const;

export function ClientListPage() {
  const navigate = useNavigate();

  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [clientType, setClientType] = useState('');
  const [owner, setOwner] = useState('');

  // Debounced: `search` runs server-side against a scoped queryset, and a request per
  // keystroke is a request per keystroke against the whole register.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const list = usePaginatedList<ClientListItem>(qk.list('clients'), STAFF.clients, {
    search: debouncedSearch || undefined,
    status: status || undefined,
    client_type: clientType || undefined,
    owner: owner || undefined,
  });

  // A changed filter applied at an old cursor shows the wrong slice of the new result
  // set, so the cursor resets whenever the query changes.
  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [debouncedSearch, status, clientType, owner, resetCursor]);

  // The list carries `responsible_lawyer` as a bare `User` id. Resolving it needs the
  // firm directory, which not every reader may see — hence the em dash fallback rather
  // than an apology.
  const members = useFirmMembers();

  const columns: Column<ClientListItem>[] = [
    {
      key: 'reference',
      header: 'Reference',
      width: 'w-32',
      render: (row) => (
        <Link
          to={`/clients/${row.id}`}
          className="font-medium text-primary hover:underline"
          // The row is clickable, but a real link is what gives middle-click,
          // "open in new tab" and a status-bar URL.
          onClick={(event) => event.stopPropagation()}
        >
          {row.reference}
        </Link>
      ),
    },
    {
      key: 'display_name',
      header: 'Name',
      render: (row) => <span className="text-ink">{row.display_name}</span>,
    },
    {
      key: 'client_type',
      header: 'Type',
      hideOnMobile: true,
      render: (row) => (
        <Badge tone="neutral" size="sm">
          {row.client_type === 'organization' ? 'Organisation' : 'Individual'}
        </Badge>
      ),
    },
    {
      key: 'primary_contact_name',
      header: 'Primary contact',
      hideOnMobile: true,
      render: (row) =>
        row.primary_contact_name ? (
          <span className="text-ink">{row.primary_contact_name}</span>
        ) : (
          // Nobody designated. On a joint or corporate client this is the state in
          // which correspondence goes to whoever was asked last, so it is flagged.
          <Badge tone="warning" size="sm">
            None designated
          </Badge>
        ),
    },
    {
      key: 'responsible_lawyer',
      header: 'Responsible',
      hideOnMobile: true,
      render: (row) =>
        row.responsible_lawyer ? (
          <span className="text-ink">{members.nameFor(row.responsible_lawyer)}</span>
        ) : (
          <Badge tone="warning" size="sm">
            Unassigned
          </Badge>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusPill label={humanise(row.status)} tone={STATUS_TONES[row.status] ?? 'neutral'} />
          {row.portal_enabled && (
            <Badge tone="info" size="sm">
              Portal
            </Badge>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Clients"
        description="Every client relationship you are permitted to see."
        actions={
          <Can module="client" action="create">
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setImporting(true)}>
                Import
              </Button>
              <Button variant="primary" onClick={() => navigate('/clients/new')}>
                New client
              </Button>
            </div>
          </Can>
        }
      />

      <ImportCsvDialog
        kind="clients"
        open={importing}
        onClose={() => setImporting(false)}
      />

      <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label="Search"
          hideLabel
          type="search"
          placeholder="Search reference, name or contact"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select
          label="Status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
        <Select
          label="Type"
          hideLabel
          options={TYPE_OPTIONS}
          value={clientType}
          onChange={(event) => setClientType(event.target.value)}
        />
        <Select
          label="Owner"
          hideLabel
          options={OWNER_OPTIONS}
          value={owner}
          onChange={(event) => setOwner(event.target.value)}
        />
      </div>

      <Table
        caption="Client list"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => navigate(`/clients/${row.id}`)}
        emptyTitle="No clients to show"
        emptyDescription="Nothing matches these filters."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />
    </>
  );
}
